import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { apiBaseUrl, authBaseUrl } from '../../config/runtime';
import { formatDuration } from '../../utils/duration';
import AppModal from '../common/AppModal';
import AlertStack from '../common/AlertStack';
import './TicketManager.css';

const STATUS_FILTER_OPTIONS = [
  { value: 'todos', label: 'Todos' },
  { value: 'resolvido', label: 'Resolvido' },
  { value: 'ag_issue_correcao', label: 'Ag. Issue Correcao' },
  { value: 'ag_envio', label: 'Ag. Envio' },
  { value: 'enviado', label: 'Enviado' },
  { value: 'suspenso', label: 'Suspenso' },
  { value: 'pendente_retorno_cliente', label: 'Pendente Retorno Cliente' },
  { value: 'encerrado', label: 'Encerrado' },
  { value: 'finalizado', label: 'Finalizado' },
];

const STATUS_FORM_OPTIONS = [
  { value: 'com_robo', label: 'Com Robo' },
  { value: 'em_disparo', label: 'Em Disparo' },
  { value: 'em_aberto', label: 'Em Aberto' },
  { value: 'visualizado', label: 'Visualizado' },
  { value: 'resolvido', label: 'Resolvido' },
  { value: 'ag_issue_correcao', label: 'Ag. Issue Correcao' },
  { value: 'ag_envio', label: 'Ag. Envio' },
  { value: 'enviado', label: 'Enviado' },
  { value: 'suspenso', label: 'Suspenso' },
  { value: 'pendente_retorno_cliente', label: 'Pendente Retorno Cliente' },
  { value: 'em_finalizacao', label: 'Em Finalizacao' },
  { value: 'encerrado', label: 'Encerrado' },
  { value: 'finalizado', label: 'Finalizado' },
];

const STATUS_LABEL_MAP = [...STATUS_FILTER_OPTIONS, ...STATUS_FORM_OPTIONS].reduce((acc, item) => {
  acc[item.value] = item.label;
  return acc;
}, {});

const SORTABLE_FIELDS = new Set([
  'ticket_number',
  'customer_phone',
  'customer_name',
  'opening_attendant',
  'closing_attendant',
  'opened_at',
  'closed_at',
  'category',
  'avg_response_seconds',
  'nps_average',
  'status',
  'last_timestamp',
]);

const ALERT_ROLE_OPTIONS = [
  { value: 'master', label: 'Master' },
  { value: 'admin', label: 'Administrador' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'atendente', label: 'Atendente' },
];

const ALERT_HIDDEN_STATUSES = new Set(['finalizado', 'encerrado']);

const TICKET_DETAIL_SECTIONS = [
  { value: 'customer', label: 'Cliente' },
  { value: 'classification', label: 'Classificacao' },
  { value: 'operations', label: 'Operacoes' },
  { value: 'history', label: 'Historico' },
];

const DEFAULT_ALERT_SETTINGS = {
  enabled: true,
  alert_gc_open: true,
  alert_sla_breached: true,
  alert_sla_risk: true,
  sla_risk_threshold: 90,
  recipient_users: [],
  recipient_roles: ['master', 'admin', 'supervisor'],
};

const parseDateValue = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const computeSlaProjection = (ticket) => {
  const dueDate = parseDateValue(ticket?.sla_due);
  if (!dueDate) {
    return {
      progress: null,
      remainingSeconds: null,
      state: 'sem_sla',
    };
  }

  const now = Date.now();
  const openedDate = parseDateValue(ticket?.opened_at) || parseDateValue(ticket?.last_timestamp);
  let startMs = openedDate ? openedDate.getTime() : null;
  const dueMs = dueDate.getTime();
  if (!startMs || startMs >= dueMs) {
    const fallbackWindowMs = 24 * 60 * 60 * 1000;
    startMs = dueMs - fallbackWindowMs;
  }

  const totalMs = Math.max(1, dueMs - startMs);
  const elapsedMs = Math.max(0, now - startMs);
  const progress = Math.max(0, Math.min(999, (elapsedMs / totalMs) * 100));
  const remainingSeconds = Math.floor((dueMs - now) / 1000);

  if (remainingSeconds <= 0) {
    return { progress, remainingSeconds, state: 'breached' };
  }
  if (progress >= 90) {
    return { progress, remainingSeconds, state: 'risk' };
  }
  return { progress, remainingSeconds, state: 'ok' };
};

const TicketManager = ({ chats, userId, onPullSupport }) => {
  const token = localStorage.getItem('token');
  const companyCode = localStorage.getItem('companyCode') || '';
  const currentUserId = String(localStorage.getItem('userId') || '').toLowerCase();
  const currentRole = String(localStorage.getItem('role') || '').toLowerCase();

  const [managementTickets, setManagementTickets] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState('');
  const [pendingAutoCloseHours, setPendingAutoCloseHours] = useState(72);

  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [categoryFilter, setCategoryFilter] = useState('todas');
  const [sortBy, setSortBy] = useState('last_timestamp');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetailSection, setTicketDetailSection] = useState('customer');
  const [ticketDetails, setTicketDetails] = useState(null);
  const [ticketNotes, setTicketNotes] = useState([]);
  const [newTicketNote, setNewTicketNote] = useState('');
  const [loadingTicket, setLoadingTicket] = useState(false);
  const [operationNote, setOperationNote] = useState('');
  const [operationLoading, setOperationLoading] = useState('');
  const [ticketForm, setTicketForm] = useState({
    status: '',
    priority: '',
    category: '',
    channel: '',
    impact: '',
    urgency: '',
    sla_due: '',
    resolution_summary: '',
    gc_reason: '',
  });
  const [alertPanelOpen, setAlertPanelOpen] = useState(false);
  const [alertPanelSection, setAlertPanelSection] = useState('rules');
  const [alertSettings, setAlertSettings] = useState(DEFAULT_ALERT_SETTINGS);
  const [alertSettingsLoading, setAlertSettingsLoading] = useState(false);
  const [alertSettingsSaving, setAlertSettingsSaving] = useState(false);
  const [alertSettingsError, setAlertSettingsError] = useState('');
  const [availableRecipients, setAvailableRecipients] = useState([]);
  const [popupAlerts, setPopupAlerts] = useState([]);
  const [finalizeModalOpen, setFinalizeModalOpen] = useState(false);
  const [finalizeTargetTicket, setFinalizeTargetTicket] = useState(null);
  const [finalizeCategory, setFinalizeCategory] = useState('');
  const [finalizeSubmitting, setFinalizeSubmitting] = useState(false);
  const activeAlertKeysRef = useRef(new Set());

  const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString('pt-BR');
  };

  const getAuthHeaders = useCallback(() => {
    if (!token) return null;
    return {
      Authorization: `Bearer ${token}`,
      ...(companyCode ? { 'X-Company-Code': companyCode } : {}),
    };
  }, [token, companyCode]);

  const loadAlertSettings = useCallback(async () => {
    try {
      setAlertSettingsLoading(true);
      setAlertSettingsError('');
      const res = await axios.get(`${apiBaseUrl}/management/alerts/settings`);
      const payload = res.data || {};
      setAlertSettings({
        ...DEFAULT_ALERT_SETTINGS,
        ...payload,
        recipient_users: Array.isArray(payload.recipient_users)
          ? payload.recipient_users.map((item) => String(item).toLowerCase())
          : DEFAULT_ALERT_SETTINGS.recipient_users,
        recipient_roles: Array.isArray(payload.recipient_roles)
          ? payload.recipient_roles.map((item) => String(item).toLowerCase())
          : DEFAULT_ALERT_SETTINGS.recipient_roles,
      });
    } catch (error) {
      setAlertSettings(DEFAULT_ALERT_SETTINGS);
      setAlertSettingsError('Falha ao carregar regras de alertas.');
    } finally {
      setAlertSettingsLoading(false);
    }
  }, []);

  const loadRecipientUsers = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers) {
      setAvailableRecipients([]);
      return;
    }
    try {
      const res = await axios.get(`${authBaseUrl}/users`, { headers });
      const rows = Array.isArray(res.data) ? res.data : [];
      const normalized = rows
        .filter((user) => user?.active && user?.email)
        .map((user) => ({
          id: String(user.id || user.email),
          email: String(user.email || '').toLowerCase(),
          name: user.name || user.email,
          role: (user.role || 'atendente').toLowerCase(),
        }));
      setAvailableRecipients(normalized);
    } catch (error) {
      setAvailableRecipients([]);
    }
  }, [getAuthHeaders]);

  const fetchManagementTickets = useCallback(async () => {
    try {
      setLoadingList(true);
      setListError('');
      const res = await axios.get(`${apiBaseUrl}/tickets/management`);
      const rows = Array.isArray(res.data) ? res.data : [];
      setManagementTickets(rows);
      if (rows.length > 0 && rows[0].pending_auto_close_hours) {
        setPendingAutoCloseHours(rows[0].pending_auto_close_hours);
      }
    } catch (error) {
      setListError('Falha ao carregar dados de gestao dos chamados.');
      setManagementTickets([]);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    fetchManagementTickets();
    const intervalId = setInterval(fetchManagementTickets, 30000);
    return () => clearInterval(intervalId);
  }, [fetchManagementTickets]);

  useEffect(() => {
    loadAlertSettings();
    loadRecipientUsers();
  }, [loadAlertSettings, loadRecipientUsers]);

  useEffect(() => {
    if (!selectedTicket) return;
    const loadTicket = async () => {
      try {
        setLoadingTicket(true);
        const res = await axios.get(`${apiBaseUrl}/tickets/${selectedTicket}`);
        setTicketDetails(res.data);
        setTicketForm({
          status: res.data?.status || '',
          priority: res.data?.priority || '',
          category: res.data?.category || '',
          channel: res.data?.channel || '',
          impact: res.data?.impact || '',
          urgency: res.data?.urgency || '',
          sla_due: res.data?.sla_due ? res.data.sla_due.slice(0, 16) : '',
          resolution_summary: res.data?.resolution_summary || '',
          gc_reason: res.data?.customer?.gc_reason || '',
        });
        setOperationNote(res.data?.resolution_summary || '');
        const notesRes = await axios.get(`${apiBaseUrl}/tickets/${selectedTicket}/notes`);
        setTicketNotes(notesRes.data || []);
      } catch (error) {
        setTicketDetails(null);
        setTicketNotes([]);
      } finally {
        setLoadingTicket(false);
      }
    };
    loadTicket();
  }, [selectedTicket]);

  const fallbackTickets = useMemo(() => {
    return chats.map((chat) => {
      const ticketNumber = chat.TicketNumber || chat.ticket_number || chat.ticketNumber || '';
      const customerPhone = chat.customer_phone || (chat.chatId ? chat.chatId.split('@')[0] : '');
      return {
        ticket_number: ticketNumber,
        chat_id: chat.chatId,
        status: chat.status,
        category: chat.category,
        priority: chat.priority || null,
        sla_due: chat.sla_due || null,
        last_message: chat.lastMessage,
        last_timestamp: chat.lastTimestamp,
        customer_phone: customerPhone,
        customer_name: chat.customer_name || chat.name || 'Cliente',
        opening_attendant: chat.atendente || 'Sem atendente',
        closing_attendant: '-',
        opened_at: null,
        closed_at: null,
        avg_response_seconds: null,
        nps_average: null,
        current_attendant: chat.atendente || '-',
        is_gc: false,
        gc_reason: null,
        protocol_count: 0,
        sla_progress_percent: null,
        sla_remaining_seconds: null,
        sla_state: 'sem_sla',
      };
    });
  }, [chats]);

  const baseRows = managementTickets.length > 0 ? managementTickets : fallbackTickets;

  const categoryFilterOptions = useMemo(() => {
    const categoryMap = new Map();
    baseRows.forEach((ticket) => {
      const normalized = (ticket.category || '').trim();
      if (!normalized) return;
      const key = normalized.toLowerCase();
      if (!categoryMap.has(key)) {
        categoryMap.set(key, normalized);
      }
    });
    const dynamicOptions = [...categoryMap.entries()]
      .sort((a, b) => a[1].localeCompare(b[1], 'pt-BR'))
      .map(([value, label]) => ({ value, label }));
    return [{ value: 'todas', label: 'Todas categorias' }, ...dynamicOptions];
  }, [baseRows]);

  const filtered = useMemo(() => {
    return baseRows.filter((ticket) => {
      const ticketNumber = ticket.ticket_number || '';
      const phone = ticket.customer_phone || '';
      const name = ticket.customer_name || '';
      const text = `${name} ${phone} ${ticketNumber}`.toLowerCase();
      const matchesQuery = text.includes(query.toLowerCase());
      const matchesStatus = statusFilter === 'todos' ? true : ticket.status === statusFilter;
      const ticketCategory = (ticket.category || '').trim().toLowerCase();
      const matchesCategory = categoryFilter === 'todas' ? true : ticketCategory === categoryFilter;
      return matchesQuery && matchesStatus && matchesCategory;
    });
  }, [baseRows, query, statusFilter, categoryFilter]);

  const getSortValue = (ticket, field) => {
    if (!ticket || !field) return null;
    switch (field) {
      case 'opened_at':
      case 'closed_at':
      case 'last_timestamp': {
        const raw = ticket[field];
        if (!raw) return null;
        const parsed = new Date(raw).getTime();
        return Number.isNaN(parsed) ? null : parsed;
      }
      case 'avg_response_seconds':
      case 'nps_average': {
        const value = ticket[field];
        return value === null || value === undefined ? null : Number(value);
      }
      default: {
        const raw = ticket[field];
        if (raw === null || raw === undefined) return null;
        return String(raw).toLowerCase();
      }
    }
  };

  const sortedRows = useMemo(() => {
    const rows = [...filtered];
    if (!SORTABLE_FIELDS.has(sortBy)) return rows;
    rows.sort((a, b) => {
      const aValue = getSortValue(a, sortBy);
      const bValue = getSortValue(b, sortBy);
      const aNil = aValue === null || aValue === undefined || aValue === '';
      const bNil = bValue === null || bValue === undefined || bValue === '';
      if (aNil && bNil) return 0;
      if (aNil) return 1;
      if (bNil) return -1;

      let base = 0;
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        base = aValue - bValue;
      } else {
        base = String(aValue).localeCompare(String(bValue), 'pt-BR', { numeric: true, sensitivity: 'base' });
      }
      return sortDir === 'asc' ? base : -base;
    });
    return rows;
  }, [filtered, sortBy, sortDir]);

  const managementKpis = useMemo(() => {
    const rows = baseRows || [];
    const openRows = rows.filter((ticket) => !ALERT_HIDDEN_STATUSES.has(String(ticket?.status || '').toLowerCase()));
    const gcActive = openRows.filter((ticket) => Boolean(ticket?.is_gc)).length;
    const slaRisk = openRows.filter((ticket) => {
      const fallback = computeSlaProjection(ticket);
      const state = ticket?.sla_state ?? fallback.state;
      const remaining = ticket?.sla_remaining_seconds ?? fallback.remainingSeconds;
      return state === 'risk' || state === 'breached' || Number(remaining) <= 0;
    }).length;
    const avgResponseRows = rows.filter((ticket) => ticket?.avg_response_seconds !== null && ticket?.avg_response_seconds !== undefined);
    const avgResponseSeconds = avgResponseRows.length
      ? avgResponseRows.reduce((acc, ticket) => acc + Number(ticket.avg_response_seconds || 0), 0) / avgResponseRows.length
      : null;
    return {
      total: rows.length,
      open: openRows.length,
      gcActive,
      slaRisk,
      avgResponseSeconds,
    };
  }, [baseRows]);

  const isRecipientAllowed = useMemo(() => {
    const recipientUsers = (alertSettings.recipient_users || []).map((item) => String(item).toLowerCase());
    const recipientRoles = (alertSettings.recipient_roles || []).map((item) => String(item).toLowerCase());
    if (recipientUsers.length === 0 && recipientRoles.length === 0) {
      return true;
    }
    if (currentUserId && recipientUsers.includes(currentUserId)) {
      return true;
    }
    if (currentRole && recipientRoles.includes(currentRole)) {
      return true;
    }
    return false;
  }, [alertSettings.recipient_roles, alertSettings.recipient_users, currentRole, currentUserId]);

  const buildTicketAlerts = useCallback((ticket) => {
    const normalizedStatus = String(ticket?.status || '').toLowerCase();
    if (ALERT_HIDDEN_STATUSES.has(normalizedStatus)) {
      return [];
    }

    const ticketNumber = ticket?.ticket_number || '-';
    const customerName = ticket?.customer_name || 'Cliente';
    const alerts = [];
    const threshold = Math.max(1, Math.min(100, Number(alertSettings.sla_risk_threshold || 90)));
    const fallbackSlaProjection = computeSlaProjection(ticket);
    const slaProjection = {
      progress: ticket?.sla_progress_percent ?? fallbackSlaProjection.progress,
      remainingSeconds: ticket?.sla_remaining_seconds ?? fallbackSlaProjection.remainingSeconds,
      state: ticket?.sla_state ?? fallbackSlaProjection.state,
    };

    if (alertSettings.alert_gc_open && ticket?.is_gc) {
      alerts.push({
        key: `gc:${ticketNumber}`,
        severity: 'critical',
        title: 'Gestao de Crise Ativa (GC)',
        message: `Ticket #${ticketNumber} marcado como crise. Priorize a tratativa na Gestao.`,
        ticketNumber,
        customerName,
      });
    }

    if (alertSettings.alert_sla_breached && ticket?.sla_due && Number(slaProjection.remainingSeconds) <= 0) {
      alerts.push({
        key: `sla_breached:${ticketNumber}`,
        severity: 'critical',
        title: 'SLA Estourado',
        message: `Ticket #${ticketNumber} esta fora do SLA. Acione o atendimento responsavel imediatamente.`,
        ticketNumber,
        customerName,
      });
    } else if (
      alertSettings.alert_sla_risk &&
      ticket?.sla_due &&
      Number(slaProjection.remainingSeconds) > 0 &&
      Number(slaProjection.progress) >= threshold &&
      slaProjection.state !== 'breached'
    ) {
      alerts.push({
        key: `sla_risk:${ticketNumber}`,
        severity: 'warn',
        title: `SLA em ${Math.round(Number(slaProjection.progress) || 0)}%`,
        message: `Ticket #${ticketNumber} atingiu o limiar de risco configurado (${threshold}%).`,
        ticketNumber,
        customerName,
      });
    }

    return alerts;
  }, [alertSettings.alert_gc_open, alertSettings.alert_sla_breached, alertSettings.alert_sla_risk, alertSettings.sla_risk_threshold]);

  useEffect(() => {
    if (!alertSettings.enabled || !isRecipientAllowed) {
      setPopupAlerts([]);
      activeAlertKeysRef.current = new Set();
      return;
    }

    const activeAlerts = sortedRows.flatMap((ticket) => buildTicketAlerts(ticket));
    const nextActiveKeys = new Set(activeAlerts.map((alert) => alert.key));
    const previousActiveKeys = activeAlertKeysRef.current;
    const newAlerts = activeAlerts.filter((alert) => !previousActiveKeys.has(alert.key));

    setPopupAlerts((prev) => {
      const stillValid = prev.filter((alert) => nextActiveKeys.has(alert.key));
      const merged = [...newAlerts, ...stillValid.filter((alert) => !newAlerts.some((item) => item.key === alert.key))];
      return merged.slice(0, 6);
    });

    activeAlertKeysRef.current = nextActiveKeys;
  }, [alertSettings.enabled, buildTicketAlerts, isRecipientAllowed, sortedRows]);

  const updateAlertField = (field, value) => {
    setAlertSettings((prev) => ({ ...prev, [field]: value }));
  };

  const toggleAlertRole = (role) => {
    setAlertSettings((prev) => {
      const current = new Set((prev.recipient_roles || []).map((item) => String(item).toLowerCase()));
      if (current.has(role)) {
        current.delete(role);
      } else {
        current.add(role);
      }
      return { ...prev, recipient_roles: [...current] };
    });
  };

  const toggleAlertUser = (email) => {
    const normalized = String(email || '').toLowerCase();
    setAlertSettings((prev) => {
      const current = new Set((prev.recipient_users || []).map((item) => String(item).toLowerCase()));
      if (current.has(normalized)) {
        current.delete(normalized);
      } else {
        current.add(normalized);
      }
      return { ...prev, recipient_users: [...current] };
    });
  };

  const saveAlertSettings = async () => {
    try {
      setAlertSettingsSaving(true);
      setAlertSettingsError('');
      const parsedThreshold = Number(alertSettings.sla_risk_threshold || 90);
      const normalizedThreshold = Number.isFinite(parsedThreshold)
        ? Math.max(1, Math.min(100, parsedThreshold))
        : 90;
      const payload = {
        ...alertSettings,
        sla_risk_threshold: normalizedThreshold,
        recipient_users: (alertSettings.recipient_users || []).map((item) => String(item).toLowerCase()),
        recipient_roles: (alertSettings.recipient_roles || []).map((item) => String(item).toLowerCase()),
        updated_by: localStorage.getItem('userId') || userId || null,
      };
      const res = await axios.put(`${apiBaseUrl}/management/alerts/settings`, payload);
      const refreshed = res.data || {};
      setAlertSettings({
        ...DEFAULT_ALERT_SETTINGS,
        ...refreshed,
        recipient_users: Array.isArray(refreshed.recipient_users) ? refreshed.recipient_users : [],
        recipient_roles: Array.isArray(refreshed.recipient_roles) ? refreshed.recipient_roles : [],
      });
    } catch (error) {
      setAlertSettingsError(error?.response?.data?.message || 'Falha ao salvar configuracoes de alerta.');
    } finally {
      setAlertSettingsSaving(false);
    }
  };

  const dismissPopupAlert = (key) => {
    setPopupAlerts((prev) => prev.filter((alert) => alert.key !== key));
  };

  const openAlertPanel = () => {
    setAlertPanelSection('rules');
    setAlertPanelOpen(true);
  };

  const closeAlertPanel = () => {
    setAlertPanelOpen(false);
  };

  const handleAlertBackdrop = (event) => {
    if (event.target.classList.contains('ticket-alert-sheet-backdrop')) {
      closeAlertPanel();
    }
  };

  const openTicketDetail = (ticketNumber) => {
    if (!ticketNumber) return;
    setTicketDetailSection('customer');
    setSelectedTicket(ticketNumber);
  };

  const closeTicketDetail = () => {
    setSelectedTicket(null);
  };

  const handleTicketBackdrop = (event) => {
    if (event.target.classList.contains('ticket-detail-sheet-backdrop')) {
      closeTicketDetail();
    }
  };

  const handleSort = (field) => {
    if (!SORTABLE_FIELDS.has(field)) return;
    setSortBy((prevField) => {
      if (prevField === field) {
        setSortDir((prevDir) => (prevDir === 'asc' ? 'desc' : 'asc'));
        return prevField;
      }
      setSortDir(field === 'last_timestamp' || field === 'opened_at' || field === 'closed_at' ? 'desc' : 'asc');
      return field;
    });
  };

  const renderHeadCell = (label, field) => {
    if (!field || !SORTABLE_FIELDS.has(field)) {
      return <span>{label}</span>;
    }
    const isActive = sortBy === field;
    const arrow = isActive ? (sortDir === 'asc' ? '↑' : '↓') : '↕';
    return (
      <button
        type="button"
        className={`ticket-table__head-btn ${isActive ? 'is-active' : ''}`}
        onClick={() => handleSort(field)}
        title={`Ordenar por ${label}`}
      >
        <span>{label}</span>
        <small>{arrow}</small>
      </button>
    );
  };

  const refreshSelectedTicket = useCallback(async () => {
    if (!selectedTicket) return;
    const res = await axios.get(`${apiBaseUrl}/tickets/${selectedTicket}`);
    setTicketDetails(res.data || null);
    setTicketForm((prev) => ({
      ...prev,
      status: res.data?.status || '',
      priority: res.data?.priority || '',
      category: res.data?.category || '',
      channel: res.data?.channel || '',
      impact: res.data?.impact || '',
      urgency: res.data?.urgency || '',
      sla_due: res.data?.sla_due ? res.data.sla_due.slice(0, 16) : '',
      resolution_summary: res.data?.resolution_summary || '',
      gc_reason: res.data?.customer?.gc_reason || '',
    }));
  }, [selectedTicket]);

  const handleFinalize = (ticket) => {
    setListError('');
    setFinalizeTargetTicket(ticket || null);
    setFinalizeCategory((ticket?.category || '').trim());
    setFinalizeModalOpen(true);
  };

  const cancelFinalize = () => {
    setFinalizeModalOpen(false);
    setFinalizeTargetTicket(null);
    setFinalizeCategory('');
    setFinalizeSubmitting(false);
  };

  const submitFinalize = async () => {
    const ticket = finalizeTargetTicket;
    const chatId = ticket?.chat_id;
    const ticketNumber = ticket?.ticket_number;
    const category = finalizeCategory.trim();
    if (!chatId) return;
    if (!category) {
      setListError('Defina e confirme a categoria antes de finalizar o atendimento.');
      return;
    }
    try {
      setFinalizeSubmitting(true);
      setListError('');
      await axios.post(`${apiBaseUrl}/end-human-support`, {
        chatId,
        category,
        confirm_category: true,
        atendente: localStorage.getItem('attendantName') || 'Atendente',
      });
      await fetchManagementTickets();
      if (selectedTicket && selectedTicket === ticketNumber) {
        await refreshSelectedTicket();
      }
      cancelFinalize();
    } catch (error) {
      setListError(error?.response?.data?.message || 'Falha ao finalizar atendimento.');
    } finally {
      setFinalizeSubmitting(false);
    }
  };

  const handleSaveTicket = async () => {
    if (!selectedTicket) return;
    try {
      await axios.put(`${apiBaseUrl}/tickets/${selectedTicket}`, {
        ...ticketForm,
        sla_due: ticketForm.sla_due ? new Date(ticketForm.sla_due).toISOString() : null,
      });
      await fetchManagementTickets();
      const detailsRes = await axios.get(`${apiBaseUrl}/tickets/${selectedTicket}`);
      setTicketDetails(detailsRes.data || null);
    } catch (error) {}
  };

  const handleAddTicketNote = async () => {
    if (!selectedTicket || !newTicketNote.trim()) return;
    try {
      await axios.post(`${apiBaseUrl}/tickets/${selectedTicket}/notes`, {
        body: newTicketNote,
        author: localStorage.getItem('attendantName') || 'Atendente',
      });
      const notesRes = await axios.get(`${apiBaseUrl}/tickets/${selectedTicket}/notes`);
      setTicketNotes(notesRes.data || []);
      setNewTicketNote('');
      await fetchManagementTickets();
    } catch (error) {}
  };

  const handleResolveWithNps = async () => {
    if (!selectedTicket) return;
    try {
      setOperationLoading('resolve');
      await axios.post(`${apiBaseUrl}/tickets/${selectedTicket}/resolve`, {
        category: ticketForm.category,
        resolution_summary: ticketForm.resolution_summary,
        atendente: localStorage.getItem('attendantName') || 'Atendente',
      });
      await fetchManagementTickets();
      await refreshSelectedTicket();
    } catch (error) {
      // noop
    } finally {
      setOperationLoading('');
    }
  };

  const handleCreateProtocol = async (type) => {
    if (!selectedTicket) return;
    try {
      setOperationLoading(type.toLowerCase());
      await axios.post(`${apiBaseUrl}/tickets/${selectedTicket}/protocols`, {
        type,
        details: operationNote || ticketForm.resolution_summary,
        author: localStorage.getItem('attendantName') || 'Atendente',
      });
      setOperationNote('');
      await fetchManagementTickets();
      await refreshSelectedTicket();
    } catch (error) {
      // noop
    } finally {
      setOperationLoading('');
    }
  };

  const handleToggleGc = async () => {
    const customerId = ticketDetails?.customer?.id;
    if (!customerId) return;
    try {
      setOperationLoading('gc');
      await axios.put(`${apiBaseUrl}/customers/${customerId}/crisis`, {
        is_gc: !ticketDetails?.customer?.is_gc,
        gc_reason: ticketForm.gc_reason,
      });
      await fetchManagementTickets();
      await refreshSelectedTicket();
    } catch (error) {
      // noop
    } finally {
      setOperationLoading('');
    }
  };

  return (
    <div className="ticket-manager">
      <AlertStack alerts={popupAlerts} onDismiss={dismissPopupAlert} />

      <div className="ticket-manager__header">
        <div className="ticket-manager__hero">
          <div className="ticket-manager__title">
            <h2>Gestao de Chamados</h2>
            <p>Operacao ITIL com priorizacao, compliance de categoria e rastreabilidade por ticket.</p>
          </div>
          <div className="ticket-manager__kpis">
            <article>
              <span>Total</span>
              <strong>{managementKpis.total}</strong>
            </article>
            <article>
              <span>Em aberto</span>
              <strong>{managementKpis.open}</strong>
            </article>
            <article className={managementKpis.gcActive > 0 ? 'is-critical' : ''}>
              <span>GC ativos</span>
              <strong>{managementKpis.gcActive}</strong>
            </article>
            <article className={managementKpis.slaRisk > 0 ? 'is-warn' : ''}>
              <span>SLA em risco</span>
              <strong>{managementKpis.slaRisk}</strong>
            </article>
            <article>
              <span>Retorno medio</span>
              <strong>{formatDuration(managementKpis.avgResponseSeconds, { invalidLabel: '-', zeroLabel: '0s' })}</strong>
            </article>
          </div>
        </div>
        <div className="ticket-manager__filters">
          <div className="ticket-manager__filters-main">
            <input
              type="text"
              placeholder="Buscar por cliente, telefone ou ticket"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              {STATUS_FILTER_OPTIONS.map((status) => (
                <option key={status.value} value={status.value}>{status.label}</option>
              ))}
            </select>
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              {categoryFilterOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="ticket-manager__filters-actions">
            <button className="ghost" onClick={fetchManagementTickets}>Atualizar</button>
            <button
              className={`ghost ${alertPanelOpen ? 'is-active' : ''}`}
              onClick={openAlertPanel}
            >
              Gerenciar Alertas
            </button>
          </div>
        </div>
      </div>

      {alertPanelOpen && (
        <div className="ticket-alert-sheet-backdrop" onMouseDown={handleAlertBackdrop}>
          <aside className="ticket-alert-sheet" onMouseDown={(event) => event.stopPropagation()}>
            <div className="ticket-alert-sheet__header">
              <div>
                <h3>Centro de Alertas da Gestao</h3>
                <small>Modelo lateral para configuracao clara, sem conflito com o menu principal.</small>
              </div>
              <button type="button" className="ticket-alert-sheet__close" onClick={closeAlertPanel}>
                Fechar
              </button>
            </div>

            {alertSettingsError && <p className="ticket-alert-sheet__error">{alertSettingsError}</p>}

            <div className="ticket-alert-sheet__layout">
              <nav className="ticket-alert-sheet__nav">
                <button
                  type="button"
                  className={alertPanelSection === 'rules' ? 'active' : ''}
                  onClick={() => setAlertPanelSection('rules')}
                >
                  Regras
                </button>
                <button
                  type="button"
                  className={alertPanelSection === 'recipients' ? 'active' : ''}
                  onClick={() => setAlertPanelSection('recipients')}
                >
                  Destinatarios
                </button>
              </nav>

              <section className="ticket-alert-sheet__content">
                {alertPanelSection === 'rules' && (
                  <>
                    <div className="ticket-alert-sheet__hint">
                      Tickets em <strong>finalizado</strong> e <strong>encerrado</strong> nunca geram pop-up.
                    </div>
                    <div className="ticket-alert-settings__grid">
                      <label className="ticket-alert-toggle">
                        <input
                          type="checkbox"
                          checked={Boolean(alertSettings.enabled)}
                          onChange={(e) => updateAlertField('enabled', e.target.checked)}
                          disabled={alertSettingsLoading}
                        />
                        <span>Ativar pop-ups de gestao</span>
                      </label>
                      <label className="ticket-alert-toggle">
                        <input
                          type="checkbox"
                          checked={Boolean(alertSettings.alert_gc_open)}
                          onChange={(e) => updateAlertField('alert_gc_open', e.target.checked)}
                          disabled={alertSettingsLoading}
                        />
                        <span>GC aberto</span>
                      </label>
                      <label className="ticket-alert-toggle">
                        <input
                          type="checkbox"
                          checked={Boolean(alertSettings.alert_sla_breached)}
                          onChange={(e) => updateAlertField('alert_sla_breached', e.target.checked)}
                          disabled={alertSettingsLoading}
                        />
                        <span>SLA estourado</span>
                      </label>
                      <label className="ticket-alert-toggle">
                        <input
                          type="checkbox"
                          checked={Boolean(alertSettings.alert_sla_risk)}
                          onChange={(e) => updateAlertField('alert_sla_risk', e.target.checked)}
                          disabled={alertSettingsLoading}
                        />
                        <span>SLA em risco (limiar)</span>
                      </label>
                      <label className="ticket-alert-input">
                        <span>Limiar SLA de risco (%)</span>
                        <input
                          type="number"
                          min="1"
                          max="100"
                          value={alertSettings.sla_risk_threshold}
                          onChange={(e) => updateAlertField('sla_risk_threshold', e.target.value)}
                          disabled={alertSettingsLoading}
                        />
                      </label>
                    </div>
                  </>
                )}

                {alertPanelSection === 'recipients' && (
                  <div className="ticket-alert-settings__recipients">
                    <div>
                      <h4>Perfis que recebem alerta</h4>
                      <div className="ticket-alert-tags">
                        {ALERT_ROLE_OPTIONS.map((roleOption) => (
                          <label key={roleOption.value} className="ticket-alert-tag">
                            <input
                              type="checkbox"
                              checked={(alertSettings.recipient_roles || []).includes(roleOption.value)}
                              onChange={() => toggleAlertRole(roleOption.value)}
                            />
                            <span>{roleOption.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4>Usuarios especificos</h4>
                      <div className="ticket-alert-recipient-list">
                        {availableRecipients.length === 0 && (
                          <p>Nenhum atendente carregado. Configure por perfil ou atualize a sessao.</p>
                        )}
                        {availableRecipients.map((user) => (
                          <label key={user.id} className="ticket-alert-recipient">
                            <input
                              type="checkbox"
                              checked={(alertSettings.recipient_users || []).includes(user.email)}
                              onChange={() => toggleAlertUser(user.email)}
                            />
                            <span>
                              <strong>{user.name}</strong>
                              <small>{user.email} · {user.role}</small>
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </section>
            </div>

            <div className="ticket-alert-sheet__footer">
              <button className="ghost" onClick={loadAlertSettings} disabled={alertSettingsSaving}>
                Recarregar
              </button>
              <button onClick={saveAlertSettings} disabled={alertSettingsSaving}>
                {alertSettingsSaving ? 'Salvando...' : 'Salvar alertas'}
              </button>
            </div>
          </aside>
        </div>
      )}

      {statusFilter === 'pendente_retorno_cliente' && (
        <div className="ticket-manager__info">
          Pendente Retorno Cliente: encerramento automatico em {pendingAutoCloseHours}h sem interacao.
        </div>
      )}
      {listError && <div className="ticket-manager__error">{listError}</div>}

      <div className="ticket-table">
        <div className="ticket-table__row ticket-table__head">
          {renderHeadCell('Ticket', 'ticket_number')}
          {renderHeadCell('Cliente', 'customer_phone')}
          {renderHeadCell('Nome Cliente', 'customer_name')}
          {renderHeadCell('Atendente Abertura', 'opening_attendant')}
          {renderHeadCell('Atendente Encerramento', 'closing_attendant')}
          {renderHeadCell('Data Abertura', 'opened_at')}
          {renderHeadCell('Data Encerramento', 'closed_at')}
          {renderHeadCell('Categoria ITIL', 'category')}
          {renderHeadCell('Tempo Medio Retorno', 'avg_response_seconds')}
          {renderHeadCell('NPS (Media)', 'nps_average')}
          {renderHeadCell('Status Fluxo', 'status')}
          <span>Acoes</span>
        </div>
        {loadingList && (
          <div className="ticket-table__empty">
            Carregando tickets...
          </div>
        )}
        {!loadingList && sortedRows.length === 0 && (
          <div className="ticket-table__empty">
            Nenhum ticket encontrado para os filtros atuais.
          </div>
        )}
        {!loadingList && sortedRows.map((ticket) => {
          const ticketNumber = ticket.ticket_number || null;
          const canOpen = Boolean(ticketNumber);
          const canFinalize = Boolean(ticket.chat_id);
          const normalizedStatus = ticket.status || 'sem_status';
          const statusLabel = STATUS_LABEL_MAP[normalizedStatus] || normalizedStatus;
          return (
            <div
              key={`${ticket.chat_id || 'chat'}-${ticketNumber || 'sem-ticket'}`}
              className={`ticket-table__row ${ticket.is_gc ? 'ticket-table__row--gc' : ''}`}
            >
              <span className="ticket-chip-wrap">
                <span className="ticket-chip">{ticketNumber ? `#${ticketNumber}` : 'Sem ticket'}</span>
                {ticket.is_gc && <span className="ticket-gc-flag">GC</span>}
              </span>
              <span className="ticket-phone">{ticket.customer_phone || '-'}</span>
              <span className="ticket-client">
                <strong>{ticket.customer_name || 'Cliente'}</strong>
                <small>
                  {ticket.current_attendant || '-'}
                  {ticket.is_gc ? ' · GC Ativo' : ''}
                </small>
              </span>
              <span>{ticket.opening_attendant || '-'}</span>
              <span>{ticket.closing_attendant || '-'}</span>
              <span>{formatDate(ticket.opened_at)}</span>
              <span>{formatDate(ticket.closed_at)}</span>
              <span className="ticket-category-block">
                <strong className={`ticket-category-pill ${ticket.category_confirmed ? 'is-confirmed' : 'is-pending'}`}>
                  {ticket.category || 'Sem categoria'}
                </strong>
                <small className={`ticket-category-confirm ${ticket.category_confirmed ? 'is-confirmed' : 'is-pending'}`}>
                  {ticket.category_confirmed ? 'Confirmada' : 'Pendente'}
                </small>
              </span>
              <span>{formatDuration(ticket.avg_response_seconds, { invalidLabel: '-', zeroLabel: '-' })}</span>
              <span className="ticket-nps">{ticket.nps_average !== null && ticket.nps_average !== undefined ? Number(ticket.nps_average).toFixed(1) : '-'}</span>
              <span className={`ticket-status status-${normalizedStatus}`}>{statusLabel}</span>
              <span className="ticket-actions">
                <button className="ghost" disabled={!canOpen} onClick={() => canOpen && openTicketDetail(ticketNumber)}>Abrir</button>
                {(normalizedStatus === 'com_robo' || normalizedStatus === 'em_disparo') && (
                  <button disabled={!canOpen} onClick={() => canOpen && onPullSupport(ticketNumber)}>Puxar</button>
                )}
                {(normalizedStatus === 'em_aberto' || normalizedStatus === 'visualizado' || normalizedStatus === 'resolvido') && (
                  <button className="ghost" disabled={!canFinalize} onClick={() => canFinalize && handleFinalize(ticket)}>Finalizar</button>
                )}
              </span>
            </div>
          );
        })}
      </div>

      {selectedTicket && (
        <div className="ticket-detail-sheet-backdrop" onMouseDown={handleTicketBackdrop}>
          <aside className="ticket-detail-sheet" onMouseDown={(event) => event.stopPropagation()}>
            <div className="ticket-drawer__header">
              <div>
                <h3>Ticket #{selectedTicket}</h3>
                <p>{ticketDetails?.customer?.name || 'Cliente'} · {ticketDetails?.customer?.phone || 'Sem telefone'}</p>
              </div>
              <button className="ticket-drawer__close" onClick={closeTicketDetail}>
                Fechar
              </button>
            </div>

            <div className="ticket-detail-sheet__layout">
              <nav className="ticket-detail-sheet__nav">
                {TICKET_DETAIL_SECTIONS.map((section) => (
                  <button
                    key={section.value}
                    type="button"
                    className={ticketDetailSection === section.value ? 'active' : ''}
                    onClick={() => setTicketDetailSection(section.value)}
                  >
                    {section.label}
                  </button>
                ))}
              </nav>

              {loadingTicket ? (
                <div className="ticket-drawer__loading">Carregando detalhes do ticket...</div>
              ) : (
                <div className="ticket-drawer__content">
                  {ticketDetailSection === 'customer' && (
                    <div className="ticket-drawer__section">
                      <h4>Informacoes do Cliente</h4>
                      <div className="ticket-drawer__grid">
                        <div><strong>Nome:</strong> {ticketDetails?.customer?.name || 'Cliente'}</div>
                        <div><strong>Telefone:</strong> {ticketDetails?.customer?.phone || 'Sem telefone'}</div>
                        <div><strong>Email:</strong> {ticketDetails?.customer?.email || '-'}</div>
                        <div><strong>Documento:</strong> {ticketDetails?.customer?.document || '-'}</div>
                        <div><strong>Endereco:</strong> {ticketDetails?.customer?.address || '-'}</div>
                        <div><strong>Empresa:</strong> {ticketDetails?.company?.name || '-'}</div>
                        <div><strong>Gestao de Crise:</strong> {ticketDetails?.customer?.is_gc ? 'Ativa' : 'Inativa'}</div>
                        <div><strong>Motivo GC:</strong> {ticketDetails?.customer?.gc_reason || '-'}</div>
                      </div>
                    </div>
                  )}

                  {ticketDetailSection === 'classification' && (
                    <div className="ticket-drawer__section">
                      <h4>Classificacao (ITIL)</h4>
                      <div className="ticket-drawer__form">
                        <select value={ticketForm.status} onChange={(e) => setTicketForm({ ...ticketForm, status: e.target.value })}>
                          <option value="">Status do fluxo</option>
                          {STATUS_FORM_OPTIONS.map((status) => (
                            <option key={status.value} value={status.value}>{status.label}</option>
                          ))}
                        </select>
                        <select value={ticketForm.priority} onChange={(e) => setTicketForm({ ...ticketForm, priority: e.target.value })}>
                          <option value="">Prioridade</option>
                          <option value="baixa">Baixa</option>
                          <option value="media">Media</option>
                          <option value="alta">Alta</option>
                          <option value="critica">Critica</option>
                        </select>
                        <input
                          value={ticketForm.category}
                          onChange={(e) => setTicketForm({ ...ticketForm, category: e.target.value })}
                          placeholder="Categoria ITIL (ex: Incidente, Requisicao)"
                        />
                        <input
                          value={ticketForm.channel}
                          onChange={(e) => setTicketForm({ ...ticketForm, channel: e.target.value })}
                          placeholder="Canal (ex: WhatsApp, Email)"
                        />
                        <select value={ticketForm.impact} onChange={(e) => setTicketForm({ ...ticketForm, impact: e.target.value })}>
                          <option value="">Impacto</option>
                          <option value="baixo">Baixo</option>
                          <option value="medio">Medio</option>
                          <option value="alto">Alto</option>
                        </select>
                        <select value={ticketForm.urgency} onChange={(e) => setTicketForm({ ...ticketForm, urgency: e.target.value })}>
                          <option value="">Urgencia</option>
                          <option value="baixa">Baixa</option>
                          <option value="media">Media</option>
                          <option value="alta">Alta</option>
                        </select>
                        <input
                          type="datetime-local"
                          value={ticketForm.sla_due}
                          onChange={(e) => setTicketForm({ ...ticketForm, sla_due: e.target.value })}
                        />
                        <textarea
                          rows="2"
                          value={ticketForm.resolution_summary}
                          onChange={(e) => setTicketForm({ ...ticketForm, resolution_summary: e.target.value })}
                          placeholder="Resumo / Solucao aplicada"
                        />
                        <div className={`ticket-category-state ${ticketDetails?.category_confirmed ? 'is-confirmed' : 'is-pending'}`}>
                          <strong>Confirmacao de categoria:</strong>{' '}
                          {ticketDetails?.category_confirmed
                            ? `confirmada por ${ticketDetails?.category_confirmed_by || 'atendente'}`
                            : 'pendente (obrigatoria antes de finalizar atendimento)'}
                        </div>
                        <button onClick={handleSaveTicket}>Salvar Ticket</button>
                      </div>
                    </div>
                  )}

                  {ticketDetailSection === 'operations' && (
                    <div className="ticket-drawer__section">
                      <h4>Operacoes de Atendimento</h4>
                      <div className="ticket-drawer__operations">
                        <textarea
                          rows="2"
                          value={operationNote}
                          onChange={(e) => setOperationNote(e.target.value)}
                          placeholder="Contexto para COR/RMA (opcional)"
                        />
                        <input
                          value={ticketForm.gc_reason}
                          onChange={(e) => setTicketForm({ ...ticketForm, gc_reason: e.target.value })}
                          placeholder="Motivo para GC"
                        />
                        <div className="ticket-drawer__operations-actions">
                          <button onClick={handleResolveWithNps} disabled={operationLoading !== ''}>
                            {operationLoading === 'resolve' ? 'Processando...' : 'Resolver + NPS'}
                          </button>
                          <button className="ghost" onClick={() => handleCreateProtocol('COR')} disabled={operationLoading !== ''}>
                            {operationLoading === 'cor' ? 'Abrindo...' : 'Abrir COR'}
                          </button>
                          <button className="ghost" onClick={() => handleCreateProtocol('RMA')} disabled={operationLoading !== ''}>
                            {operationLoading === 'rma' ? 'Abrindo...' : 'Abrir RMA'}
                          </button>
                          <button className="warn" onClick={handleToggleGc} disabled={operationLoading !== ''}>
                            {operationLoading === 'gc'
                              ? 'Salvando...'
                              : (ticketDetails?.customer?.is_gc ? 'Remover GC' : 'Marcar GC')}
                          </button>
                        </div>
                        <div className="ticket-drawer__protocol-list">
                          {(ticketDetails?.protocols || []).length === 0 && <p>Sem protocolos vinculados.</p>}
                          {(ticketDetails?.protocols || []).map((protocol) => (
                            <div key={protocol.id} className="ticket-drawer__protocol-item">
                              <strong>{protocol.protocol_code}</strong>
                              <span>{protocol.protocol_type}</span>
                              <small>
                                Projeto: {protocol.project_code || '-'}
                                {protocol.created_at ? ` · ${new Date(protocol.created_at).toLocaleString()}` : ''}
                              </small>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {ticketDetailSection === 'history' && (
                    <div className="ticket-drawer__section">
                      <h4>Historico do Ticket</h4>
                      <div className="ticket-drawer__notes">
                        <textarea
                          placeholder="Adicionar comentario interno do ticket"
                          value={newTicketNote}
                          onChange={(e) => setNewTicketNote(e.target.value)}
                          rows="2"
                        />
                        <button onClick={handleAddTicketNote}>Salvar comentario</button>
                        {ticketNotes.length === 0 && <p>Sem comentarios.</p>}
                        {ticketNotes.map((note) => (
                          <div key={note.id} className="ticket-note">
                            <div className="ticket-note__meta">
                              <span>{note.author || 'Sistema'}</span>
                              <small>{note.created_at ? new Date(note.created_at).toLocaleString() : ''}</small>
                            </div>
                            <p>{note.body}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </aside>
        </div>
      )}

      <AppModal
        open={finalizeModalOpen}
        title={`Finalizar ticket ${finalizeTargetTicket?.ticket_number ? `#${finalizeTargetTicket.ticket_number}` : ''}`}
        description="Confirme a categoria ITIL para registrar o encerramento corretamente."
        confirmText="Confirmar finalizacao"
        cancelText="Cancelar"
        onCancel={cancelFinalize}
        onConfirm={submitFinalize}
        loading={finalizeSubmitting}
      >
        <label className="ticket-finalize-field">
          <span>Categoria confirmada</span>
          <input
            type="text"
            value={finalizeCategory}
            onChange={(e) => setFinalizeCategory(e.target.value)}
            placeholder="Ex.: Incidente, Requisicao, COR, RMA"
          />
        </label>
      </AppModal>
    </div>
  );
};

export default TicketManager;
